"use client";
import { useMemo, useState } from "react";
import {
  Thermometer,
  Zap,
  ArrowRightLeft,
  BookOpen,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toNum, fmt, DEHU_CATALOG } from "@/components/calculators/calc-constants";
import { NumField, BigStat, EmailButton } from "@/components/calculators/calc-ui";

/* ── Air mover catalog re-used from page constants (re-declared locally to avoid needing export) ── */
type AirMoverModel = { id: string; label: string; cfm: number; amps: number; type: "centrifugal" | "axial" };
const AIR_MOVER_CATALOG_LOCAL: AirMoverModel[] = [
  { id: "dri-eaz-ace-turbodryer",   label: "Dri-Eaz Ace TurboDryer",    cfm: 3200, amps: 2.1, type: "centrifugal" },
  { id: "dri-eaz-sahara-pro-x2",    label: "Dri-Eaz Sahara Pro X2",     cfm: 3100, amps: 1.8, type: "centrifugal" },
  { id: "dri-eaz-velo-low-profile", label: "Dri-Eaz Velo Pro",          cfm: 2500, amps: 1.3, type: "centrifugal" },
  { id: "dri-eaz-studebaker",       label: "Dri-Eaz Studebaker",        cfm: 3400, amps: 2.9, type: "axial" },
  { id: "dri-eaz-axial-x8",         label: "Dri-Eaz X8 Axial",          cfm: 2800, amps: 2.0, type: "axial" },
  { id: "phoenix-axial-ax",         label: "Phoenix Axial AX",          cfm: 2950, amps: 2.3, type: "axial" },
  { id: "phoenix-air-max",          label: "Phoenix Air Max",           cfm: 3350, amps: 2.8, type: "centrifugal" },
  { id: "bair-vp-25",               label: "B-Air VP-25",               cfm: 2500, amps: 1.5, type: "centrifugal" },
  { id: "bair-vp-33",               label: "B-Air VP-33",               cfm: 3300, amps: 2.3, type: "centrifugal" },
  { id: "xpower-p-630",             label: "XPOWER P-630",              cfm: 2800, amps: 2.0, type: "centrifugal" },
  { id: "xpower-x-830",             label: "XPOWER X-830",              cfm: 3600, amps: 2.8, type: "centrifugal" },
  { id: "generic-snail",            label: "Generic snail (budget)",    cfm: 2400, amps: 1.6, type: "centrifugal" },
];

/* Volume units for conversion calc */
const VOLUME_UNITS: { value: string; label: string; toMl: number; shortLabel: string }[] = [
  { value: "flozUS",  label: "Fluid Ounce (US)", toMl: 29.5735,   shortLabel: "fl oz (US)" },
  { value: "pintUS",  label: "Pint (US)",         toMl: 473.176,   shortLabel: "pt (US)" },
  { value: "quartUS", label: "Quart (US)",        toMl: 946.353,   shortLabel: "qt (US)" },
  { value: "galUS",   label: "Gallon (US)",       toMl: 3785.41,   shortLabel: "gal (US)" },
  { value: "flozUK",  label: "Fluid Ounce (UK)", toMl: 28.4131,   shortLabel: "fl oz (UK)" },
  { value: "pintUK",  label: "Pint (UK)",         toMl: 568.261,   shortLabel: "pt (UK)" },
  { value: "quartUK", label: "Quart (UK)",        toMl: 1136.52,   shortLabel: "qt (UK)" },
  { value: "galUK",   label: "Gallon (UK)",       toMl: 4546.09,   shortLabel: "gal (UK)" },
  { value: "ml",      label: "Millilitre",        toMl: 1,          shortLabel: "mL" },
  { value: "l",       label: "Litre",             toMl: 1000,       shortLabel: "L" },
  { value: "ft3",     label: "Cubic foot",        toMl: 28316.85,  shortLabel: "ft³" },
  { value: "m3",      label: "Cubic metre",       toMl: 1_000_000, shortLabel: "m³" },
];

/* Magnus-Tetens formula (Sonntag 1990 coefficients).
 * Accepts any valid temperature including 0°F and sub-zero winter conditions.
 * Returns all-zero only when inputs are not finite or RH is non-positive. */
function psychroFull(tempF: number, rhPct: number) {
  if (!Number.isFinite(tempF) || !Number.isFinite(rhPct) || rhPct <= 0) {
    return { dewF: 0, gpp: 0, saturationGpp: 0, thirstGpp: 0, depressionF: 0, vaporPressureInHg: 0 };
  }
  const Tc = ((tempF - 32) * 5) / 9;
  const a = 17.625, b = 243.04;
  const alpha = Math.log(rhPct / 100) + (a * Tc) / (b + Tc);
  const dewC = (b * alpha) / (a - alpha);
  const dewF = (dewC * 9) / 5 + 32;
  const Pws = 0.61078 * Math.exp((17.27 * Tc) / (Tc + 237.3));
  const Pw = (rhPct / 100) * Pws;
  const W = (0.622 * Pw) / (101.325 - Pw);
  const Wsat = (0.622 * Pws) / (101.325 - Pws);
  const gpp = W * 7000;
  const saturationGpp = Wsat * 7000;
  const thirstGpp = Math.max(0, saturationGpp - gpp);
  const depressionF = Math.max(0, tempF - dewF);
  const vaporPressureInHg = Pw * 0.2953;
  return { dewF, gpp, saturationGpp, thirstGpp, depressionF, vaporPressureInHg };
}

/* NEC nominal residential voltage. Use 120 V for power/cost calculations
 * (older datasheets cite 115 V — that's the lower bound of acceptable range,
 * not the nominal). For branch-circuit loading safety, both yield the same
 * ampere reading; the difference only affects kWh and cost. */
const MAINS_VOLTS = 120;

type PowerRow = { id: string; kind: "dehu" | "airMover" | "nam" | "manual"; modelId: string; amps: string; count: string };
const BLANK_POWER_ROW = (): PowerRow => ({ id: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)), kind: "dehu", modelId: DEHU_CATALOG[0].id, amps: "", count: "1" });

/* ── Reference card helpers ── */
function ReferenceCard({ open, onToggle, title, preview, children }: { id: string; open: boolean; onToggle: () => void; title: string; preview: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, overflow: "hidden" }}>
      <button type="button" onClick={onToggle} style={{ width: "100%", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer", background: "transparent", border: "none", textAlign: "left", gap: 16 }}>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: "#ffffff", lineHeight: 1.4 }}>{title}</p>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.60)", marginTop: 4, lineHeight: 1.5 }}>{preview}</p>
        </div>
        <span style={{ flexShrink: 0, marginTop: 2 }}>
          {open ? <ChevronDown className="size-4" style={{ color: "#D4A84B" }} /> : <ChevronRight className="size-4" style={{ color: "#D4A84B" }} />}
        </span>
      </button>
      {open && (
        <div style={{ padding: "4px 20px 20px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          {children}
        </div>
      )}
    </div>
  );
}

function RefTable({ rows }: { rows: { label: string; detail: string; extra?: string }[] }) {
  return (
    <div style={{ marginTop: 14, borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.18)" }}>
      {rows.map((row, i) => (
        <div key={i} className="flex flex-col sm:grid sm:grid-cols-[140px_1fr] sm:items-start sm:gap-3" style={{ padding: "11px 14px", borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none", background: i % 2 === 0 ? "rgba(255,255,255,0.04)" : "transparent" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#D4A84B", lineHeight: 1.45, marginBottom: 4 }}>{row.label}</span>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.82)", lineHeight: 1.55 }}>{row.detail}{row.extra && <span style={{ display: "block", marginTop: 3, color: "rgba(255,255,255,0.55)", fontSize: 11 }}>{row.extra}</span>}</span>
        </div>
      ))}
    </div>
  );
}

function RefParagraph({ children }: { children: React.ReactNode }) {
  return <p style={{ marginTop: 12, fontSize: 13, color: "rgba(255,255,255,0.78)", lineHeight: 1.65 }}>{children}</p>;
}

/* ─────────────────────────────────────────────────────────── */
/*  Psychrometric Calc                                         */
/* ─────────────────────────────────────────────────────────── */
const colHeaderStyle: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#D4A84B", paddingLeft: 4 };
const resultHeaderRowStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.1)", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" };

function PsychroRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, alignItems: "center" }}>
      <Label style={{ color: "rgba(255,255,255,0.85)" }}>{label}</Label>
      {children}
    </div>
  );
}

function PsychroResultRow({ label, inVal, outVal, digits }: { label: string; inVal: number; outVal: number; digits: number }) {
  const delta = outVal - inVal;
  const fmtN = (n: number) => n ? fmt(n, digits) : "—";
  const deltaColor = delta > 0 ? "#f5c842" : delta < 0 ? "#9ec5ff" : "rgba(255,255,255,0.6)";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", alignItems: "center", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
      <span style={{ color: "rgba(255,255,255,0.82)" }}>{label}</span>
      <span style={{ color: "#ffffff", fontWeight: 600 }}>{fmtN(inVal)}</span>
      <span style={{ color: "#ffffff", fontWeight: 600 }}>{fmtN(outVal)}</span>
      <span style={{ color: deltaColor, fontWeight: 700 }}>{inVal && outVal ? (delta >= 0 ? "+" : "") + fmt(delta, digits) : "—"}</span>
    </div>
  );
}

function PsychroCalcSection() {
  const [inF, setInF] = useState("");
  const [inRh, setInRh] = useState("");
  const [outF, setOutF] = useState("");
  const [outRh, setOutRh] = useState("");
  const IN = useMemo(() => psychroFull(toNum(inF), toNum(inRh)), [inF, inRh]);
  const OUT = useMemo(() => psychroFull(toNum(outF), toNum(outRh)), [outF, outRh]);

  const cFromF = (v: string) => v ? fmt(((toNum(v) - 32) * 5) / 9, 1) : "";
  const setFromC = (setter: (s: string) => void) => (c: string) => {
    if (!c) return setter("");
    setter(fmt((toNum(c) * 9) / 5 + 32, 1));
  };

  return (
    <div className="flex flex-col gap-6">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, alignItems: "end" }}>
        <div />
        <div style={colHeaderStyle}>Unaffected (In)</div>
        <div style={colHeaderStyle}>Affected (Out)</div>
      </div>
      <PsychroRow label="Temperature (°F)">
        <Input type="number" inputMode="decimal" value={inF} onChange={(e) => setInF(e.target.value)} placeholder="78" />
        <Input type="number" inputMode="decimal" value={outF} onChange={(e) => setOutF(e.target.value)} placeholder="78" />
      </PsychroRow>
      <PsychroRow label="Temperature (°C)">
        <Input type="number" inputMode="decimal" value={cFromF(inF)} onChange={(e) => setFromC(setInF)(e.target.value)} placeholder="25.5" />
        <Input type="number" inputMode="decimal" value={cFromF(outF)} onChange={(e) => setFromC(setOutF)(e.target.value)} placeholder="25.5" />
      </PsychroRow>
      <PsychroRow label="Relative Humidity (%)">
        <Input type="number" inputMode="decimal" value={inRh} onChange={(e) => setInRh(e.target.value)} placeholder="45" />
        <Input type="number" inputMode="decimal" value={outRh} onChange={(e) => setOutRh(e.target.value)} placeholder="65" />
      </PsychroRow>
      <div style={{ marginTop: 8, background: "rgba(13,25,41,0.88)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, overflow: "hidden" }}>
        <div style={resultHeaderRowStyle}><span>Reading</span><span>Unaffected</span><span>Affected</span><span>Δ</span></div>
        <PsychroResultRow label="Vapor Pressure (inHg)" inVal={IN.vaporPressureInHg} outVal={OUT.vaporPressureInHg} digits={3} />
        <PsychroResultRow label="Grains (GPP)" inVal={IN.gpp} outVal={OUT.gpp} digits={1} />
        <PsychroResultRow label="Dew Point (°F)" inVal={IN.dewF} outVal={OUT.dewF} digits={1} />
        <PsychroResultRow label="Thirst — grain depression (gr/lb)" inVal={IN.thirstGpp} outVal={OUT.thirstGpp} digits={1} />
        <PsychroResultRow label="Depression — dew-point spread (°F)" inVal={IN.depressionF} outVal={OUT.depressionF} digits={1} />
      </div>
      <p style={{ color: "rgba(255,255,255,0.70)", fontSize: 12, lineHeight: 1.6 }}>
        Magnus formula for dew point + Tetens for vapor pressure. Target a <strong style={{ color: "#D4A84B" }}>30+ GPP differential</strong> between unaffected and affected areas for aggressive drying.
      </p>
      <EmailButton summary={{
        calcLabel: "Psychrometric",
        rows: [
          { label: "Unaffected (In)", value: `${toNum(inF)} °F / ${toNum(inRh)}% RH` },
          { label: "Affected (Out)", value: `${toNum(outF)} °F / ${toNum(outRh)}% RH` },
          { label: "GPP — In / Out", value: `${fmt(IN.gpp, 1)} / ${fmt(OUT.gpp, 1)}` },
          { label: "Dew Point — In / Out", value: `${fmt(IN.dewF, 1)} / ${fmt(OUT.dewF, 1)} °F` },
          { label: "Thirst — In / Out", value: `${fmt(IN.thirstGpp, 1)} / ${fmt(OUT.thirstGpp, 1)} gr/lb` },
          { label: "GPP Differential (Δ)", value: `${fmt(OUT.gpp - IN.gpp, 1)} gr/lb` },
        ],
      }} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/*  Power Calc                                                  */
/* ─────────────────────────────────────────────────────────── */
function PowerCalcSection() {
  const [rows, setRows] = useState<PowerRow[]>(() => [BLANK_POWER_ROW(), BLANK_POWER_ROW(), BLANK_POWER_ROW()]);
  const [costPerKwhCents, setCostPerKwhCents] = useState("12");
  const [hours, setHours] = useState("24");
  const [days, setDays] = useState("3");

  const rowAmps = (r: PowerRow): number => {
    const ct = Math.max(0, Math.floor(toNum(r.count)));
    if (r.kind === "manual") return toNum(r.amps) * ct;
    if (r.kind === "dehu") return (DEHU_CATALOG.find((d) => d.id === r.modelId)?.amps ?? 0) * ct;
    if (r.kind === "airMover") return (AIR_MOVER_CATALOG_LOCAL.find((d) => d.id === r.modelId)?.amps ?? 0) * ct;
    if (r.kind === "nam") return toNum(r.amps) * ct;
    return 0;
  };

  const totalAmps = useMemo(() => rows.reduce((s, r) => s + rowAmps(r), 0), [rows]);
  const kW = (totalAmps * MAINS_VOLTS) / 1000;
  const totalHours = toNum(hours) * toNum(days);
  const kWh = kW * totalHours;
  const runCost = kWh * (toNum(costPerKwhCents) / 100);

  const updateRow = (id: string, patch: Partial<PowerRow>) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => (prev.length >= 6 ? prev : [...prev, BLANK_POWER_ROW()]));
  const removeRow = (id: string) => setRows((prev) => (prev.length <= 1 ? [BLANK_POWER_ROW()] : prev.filter((r) => r.id !== id)));
  const statStyle: React.CSSProperties = { color: "#ffffff", fontWeight: 600, fontVariantNumeric: "tabular-nums" };

  return (
    <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr]">
      <div className="flex flex-col gap-4">
        {rows.map((r, i) => (
          <div key={r.id} style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "14px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#D4A84B" }}>Equipment #{i + 1}</span>
              <button type="button" onClick={() => removeRow(r.id)} aria-label="Remove row" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "rgba(255,255,255,0.7)", borderRadius: 6, padding: "4px 8px", fontSize: 11, cursor: "pointer" }}>Remove</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 80px", gap: 10, alignItems: "end" }}>
              <div className="space-y-1.5">
                <Label htmlFor={`gt-pw-kind-${r.id}`}>Type</Label>
                <Select value={r.kind} onValueChange={(v) => updateRow(r.id, { kind: v as PowerRow["kind"], modelId: v === "dehu" ? DEHU_CATALOG[0].id : v === "airMover" ? AIR_MOVER_CATALOG_LOCAL[0].id : "" })}>
                  <SelectTrigger id={`gt-pw-kind-${r.id}`} className="h-10 w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dehu">Dehumidifier</SelectItem>
                    <SelectItem value="airMover">Air Mover</SelectItem>
                    <SelectItem value="nam">Neg-Air / HEPA (manual amps)</SelectItem>
                    <SelectItem value="manual">Other (manual amps)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`gt-pw-model-${r.id}`}>{r.kind === "manual" || r.kind === "nam" ? "Amps (single unit)" : "Model"}</Label>
                {r.kind === "dehu" ? (
                  <Select value={r.modelId} onValueChange={(v) => updateRow(r.id, { modelId: v ?? "" })}>
                    <SelectTrigger id={`gt-pw-model-${r.id}`} className="h-10 w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DEHU_CATALOG.map((d) => <SelectItem key={d.id} value={d.id}>{d.label} — {d.amps}A</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : r.kind === "airMover" ? (
                  <Select value={r.modelId} onValueChange={(v) => updateRow(r.id, { modelId: v ?? "" })}>
                    <SelectTrigger id={`gt-pw-model-${r.id}`} className="h-10 w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AIR_MOVER_CATALOG_LOCAL.map((d) => <SelectItem key={d.id} value={d.id}>{d.label} — {d.amps}A</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input id={`gt-pw-model-${r.id}`} type="number" inputMode="decimal" value={r.amps} onChange={(e) => updateRow(r.id, { amps: e.target.value })} placeholder="e.g. 7.5" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`gt-pw-count-${r.id}`}># Units</Label>
                <Input id={`gt-pw-count-${r.id}`} type="number" inputMode="numeric" value={r.count} onChange={(e) => updateRow(r.id, { count: e.target.value })} />
              </div>
            </div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
              Row draw: <strong style={{ color: "#D4A84B" }}>{fmt(rowAmps(r), 1)} A</strong>
            </p>
          </div>
        ))}
        <Button variant="outline" onClick={addRow} disabled={rows.length >= 6} style={{ color: "#D4A84B", borderColor: "rgba(212,168,75,0.5)" }}>+ Add equipment</Button>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <NumField id="gt-pw-cost" label="Cost / kWh (cents)" value={costPerKwhCents} onChange={setCostPerKwhCents} placeholder="12" />
          <NumField id="gt-pw-hours" label="Hours / day" value={hours} onChange={setHours} placeholder="24" />
          <NumField id="gt-pw-days" label="Days" value={days} onChange={setDays} placeholder="3" />
        </div>
      </div>

      <div style={{ background: "rgba(13,25,41,0.88)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 14, overflow: "hidden", alignSelf: "start" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <BigStat label="Total draw" stat={`${fmt(totalAmps, 1)} A`} highlight />
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", marginTop: 4 }}>at {MAINS_VOLTS}V mains</p>
        </div>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <BigStat label="Power needed" stat={`${fmt(kW, 2)} kW`} />
          <BigStat label="Energy total" stat={`${fmt(kWh)} kWh`} />
        </div>
        <dl style={{ padding: "14px 20px", display: "grid", gridTemplateColumns: "1fr auto", rowGap: 8, columnGap: 12, fontSize: 13 }}>
          <dt style={{ color: "rgba(255,255,255,0.7)" }}>Run time</dt>
          <dd style={statStyle}>{totalHours ? `${fmt(totalHours)} hr` : "—"}</dd>
          <dt style={{ color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>Run cost</dt>
          <dd style={{ ...statStyle, color: "#D4A84B", fontWeight: 700 }}>{runCost ? `$${runCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</dd>
        </dl>
        <div style={{ padding: "0 20px 20px" }}>
          <EmailButton summary={{
            calcLabel: "Power / Amperage",
            rows: [
              { label: "Total amps", value: `${fmt(totalAmps, 1)} A` },
              { label: "Power", value: `${fmt(kW, 2)} kW` },
              { label: "Energy", value: `${fmt(kWh)} kWh` },
              { label: "Run time", value: `${fmt(totalHours)} hr` },
              { label: "Run cost", value: `$${runCost.toFixed(2)}` },
            ],
          }} />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/*  Conversions Calc                                           */
/* ─────────────────────────────────────────────────────────── */
function ConversionsCalcSection() {
  const [inputVal, setInputVal] = useState("");
  const [fromUnit, setFromUnit] = useState("galUS");
  const [perBasis, setPerBasis] = useState<"hour" | "day">("hour");

  const fromEntry = VOLUME_UNITS.find((u) => u.value === fromUnit) ?? VOLUME_UNITS[0];
  const baseMlPerHour = useMemo(() => {
    const v = toNum(inputVal);
    if (!v) return 0;
    const mlAtRate = v * fromEntry.toMl;
    return perBasis === "day" ? mlAtRate / 24 : mlAtRate;
  }, [inputVal, fromEntry.toMl, perBasis]);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 md:grid-cols-3">
        <NumField id="gt-cv-val" label="Value" value={inputVal} onChange={setInputVal} placeholder="1" />
        <div className="space-y-1.5">
          <Label htmlFor="gt-cv-from">From unit</Label>
          <Select value={fromUnit} onValueChange={(v) => setFromUnit(v ?? "")}>
            <SelectTrigger id="gt-cv-from" className="h-10 w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VOLUME_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="gt-cv-basis">Per</Label>
          <Select value={perBasis} onValueChange={(v) => setPerBasis(v as "hour" | "day")}>
            <SelectTrigger id="gt-cv-basis" className="h-10 w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hour">Per Hour</SelectItem>
              <SelectItem value="day">Per Day</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div style={{ background: "rgba(13,25,41,0.88)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
          <span>Unit</span><span style={{ textAlign: "right" }}>Per hour</span><span style={{ textAlign: "right" }}>Per day</span>
        </div>
        {VOLUME_UNITS.map((u, idx) => {
          const perHour = u.toMl > 0 ? baseMlPerHour / u.toMl : 0;
          const perDay = perHour * 24;
          const highlight = u.value === fromUnit;
          return (
            <div key={u.value} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: highlight ? "rgba(212,168,75,0.12)" : idx % 2 === 0 ? "rgba(255,255,255,0.04)" : "transparent" }}>
              <span style={{ color: highlight ? "#D4A84B" : "rgba(255,255,255,0.82)", fontSize: 13, fontWeight: highlight ? 700 : 500 }}>{u.shortLabel}</span>
              <span style={{ textAlign: "right", color: "#ffffff", fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 600 }}>{perHour ? fmt(perHour, perHour < 1 ? 4 : 2) : "—"}</span>
              <span style={{ textAlign: "right", color: "#ffffff", fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 600 }}>{perDay ? fmt(perDay, perDay < 1 ? 4 : 2) : "—"}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/*  Field Reference Guide                                      */
/* ─────────────────────────────────────────────────────────── */
function FieldRefSection() {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="flex flex-col gap-3">
      <ReferenceCard id="water-class" open={!!open["water-class"]} onToggle={() => toggle("water-class")}
        title="Water Loss Classes — What Class 1, 2, 3, and 4 Mean (IICRC S500)"
        preview="Class tells you how deeply water has soaked into building materials. It drives how many air movers and dehumidifiers you need."
      >
        <RefParagraph>The IICRC S500 standard classifies water damage by how much moisture has been absorbed into building materials — not by how much water is visible on the floor.</RefParagraph>
        <RefTable rows={[
          { label: "Class 1 — Least absorbed", detail: "Only a portion of a room is affected. Affected materials have low porosity. Little or no carpet or pad is wet. Drying factor: 0.10 pints per cubic foot per day.", extra: "Example: A toilet supply line broke but was caught within an hour. Tile floor only, no carpet, no wall penetration." },
          { label: "Class 2 — Significant absorption", detail: "An entire room of carpet and pad is wet. Walls are wet up to 24 inches. Drying factor: 0.20 pints per cubic foot per day.", extra: "Example: A dishwasher leak ran overnight and soaked the kitchen carpet and base of walls." },
          { label: "Class 3 — Greatest absorption", detail: "Water came from above — ceiling, walls, insulation, carpet, subfloor all saturated. Drying factor: 0.30 pints per cubic foot per day.", extra: "Example: An upstairs bathroom overflowed through the subfloor into the ceiling below." },
          { label: "Class 4 — Specialty drying", detail: "Very low porosity materials: hardwood floors, plaster, concrete, stone, brick. Drying factor: 0.40 pints per cubic foot per day.", extra: "Example: Hardwood floors after a flood — requires specialized drying mats and extended time." },
        ]} />
      </ReferenceCard>

      <ReferenceCard id="water-cat" open={!!open["water-cat"]} onToggle={() => toggle("water-cat")}
        title="Water Categories — Category 1, 2, and 3 (IICRC S500)"
        preview="Category tells you how contaminated the water is. It determines safety protocols, whether materials can be dried or must be removed, and the cost of the job."
      >
        <RefParagraph>The IICRC S500 standard categorizes water by its contamination level at the time of contact. Category is separate from Class — both must be assessed independently.</RefParagraph>
        <RefTable rows={[
          { label: "Category 1 — Clean water", detail: "Water from a sanitary source that poses no substantial health risk. Supply line breaks, rain intrusion, overflow from clean toilet tanks.", extra: "Materials can usually be dried in place if addressed quickly. Degrades to Category 2 within 24–48 hours if left standing." },
          { label: "Category 2 — Gray water", detail: "Contains significant contamination. Dishwasher/washing machine overflow, aquarium leak, toilet bowl overflow (urine only).", extra: "Porous materials in contact typically must be removed. PPE required." },
          { label: "Category 3 — Black water", detail: "Grossly contaminated: sewage backup, toilet overflow with feces, rising flood/river water.", extra: "All porous materials must be demolished. Full PPE, containment, and HEPA filtration required." },
        ]} />
      </ReferenceCard>

      <ReferenceCard id="ach" open={!!open["ach"]} onToggle={() => toggle("ach")}
        title="Air Changes Per Hour (ACH) — Targets by Trade"
        preview="ACH measures how many times per hour all the air in a contained space is filtered. Higher ACH = more protection."
      >
        <RefTable rows={[
          { label: "Mold (S520)", detail: "4 ACH for Level I/II. 6 ACH for Level III. 8 ACH for Level IV. 12 ACH for critical environments.", extra: "Verify negative pressure −0.02 to −0.05 inches WC with a manometer." },
          { label: "Lead remediation", detail: "4–6 ACH negative pressure with HEPA filtration on all exhaust air.", extra: "" },
          { label: "Fire / smoke", detail: "4–6 ACH for HEPA air scrubbing and odor control.", extra: "" },
          { label: "Healthcare", detail: "12–15 ACH standard patient rooms. 15+ ACH isolation / immunocompromised areas.", extra: "Requires written ICRA approval and commissioning air test." },
        ]} />
      </ReferenceCard>

      <ReferenceCard id="dehu-selection" open={!!open["dehu-selection"]} onToggle={() => toggle("dehu-selection")}
        title="Portable LGR vs. Desiccant Dehumidifiers — When to Use Each"
        preview="Standard portable dehus stop working when it gets cold or very dry. Desiccant units work in any condition."
      >
        <RefTable rows={[
          { label: "Below 45°F", detail: "Any loss in an unheated garage, warehouse, or winter building. Portables ice up and stop working." },
          { label: "Large commercial (>20,000 ft²)", detail: "Portable unit count exceeds 25–40 units — a desiccant trailer handles the whole structure from a single connection." },
          { label: "Whole-building HVAC", detail: "When ducts are wet, a desiccant unit connects to the existing duct system to dry from the inside out." },
          { label: "RH already below 35%", detail: "In Arizona summer conditions (5–15% outdoor RH), a desiccant unit can push indoor RH below 20% to pull moisture from deep materials." },
        ]} />
      </ReferenceCard>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/*  Main export                                                */
/* ─────────────────────────────────────────────────────────── */
const panelStyle = { background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, padding: "20px 22px", marginBottom: 24, boxShadow: "0 4px 24px rgba(0,0,0,0.55)" };
const sectionTitle = { fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: "#D4A84B", marginBottom: 14 };

export function GeneralToolsTab() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <Thermometer size={16} style={{ color: "#D4A84B" }} />
          <p style={{ ...sectionTitle, marginBottom: 0 }}>Psychrometrics</p>
        </div>
        <PsychroCalcSection />
      </div>

      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <Zap size={16} style={{ color: "#D4A84B" }} />
          <p style={{ ...sectionTitle, marginBottom: 0 }}>Power / Amperage</p>
        </div>
        <PowerCalcSection />
      </div>

      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <ArrowRightLeft size={16} style={{ color: "#D4A84B" }} />
          <p style={{ ...sectionTitle, marginBottom: 0 }}>Volume Conversions</p>
        </div>
        <ConversionsCalcSection />
      </div>

      <div style={{ ...panelStyle, marginBottom: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <BookOpen size={16} style={{ color: "#D4A84B" }} />
          <p style={{ ...sectionTitle, marginBottom: 0 }}>Field Reference Guide</p>
        </div>
        <FieldRefSection />
      </div>
    </div>
  );
}
